import { useForm } from '@tanstack/react-form'
import { FilterIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useChurchesStore } from '../store/churchesStore'
import type { ChurchFilter } from '@/schemas/filterSchemas'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import { Badge } from '@/components/custom/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useUsersStore } from '@/features/users/store/usersStore'
import { useIsMobile } from '@/hooks'
import IconInput from '@/components/custom/icon-input'

export default function ChurchFilter({
  onFilter,
  onCloseDrawer,
  initialFilters,
}: {
  onFilter?: (filters: ChurchFilter) => void
  onCloseDrawer?: () => void
  initialFilters: ChurchFilter
}) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)

  const users = useUsersStore((s) => s.users)
  const churches = useChurchesStore((s) => s.churches)

  const pastors = users.filter((u) => u.roles.includes('PASTOR'))
  const citiesOptions = new Set([...churches.map((c) => c.address.city)])
  const provincesOptions = new Set([...churches.map((c) => c.address.province)])

  const form = useForm({
    defaultValues: {
      search: initialFilters.search,
      pastorId: initialFilters.pastorId,
      cities: initialFilters.cities,
      provinces: initialFilters.provinces,
      onlyActives: initialFilters.onlyActives,
    },
    onSubmit: async ({ value }) => {
      onFilter?.(value)

      await new Promise((resolve) => setTimeout(resolve, 0))
      setOpen(false)
      if (isMobile && onCloseDrawer) onCloseDrawer()
    },
  })

  useEffect(() => {
    form.setFieldValue('pastorId', initialFilters.pastorId)
    form.setFieldValue('cities', initialFilters.cities)
    form.setFieldValue('provinces', initialFilters.provinces)
    form.setFieldValue('onlyActives', initialFilters.onlyActives)
  }, [initialFilters])

  const handleClear = async () => {
    form.reset()

    onFilter?.({
      pastorId: [],
      cities: [],
      provinces: [],
      onlyActives: false,
      search: isMobile ? '' : initialFilters.search,
    })

    await new Promise((resolve) => setTimeout(resolve, 0))
    setOpen(false)
  }

  const filtersActive =
    form.state.values.pastorId.length > 0 ||
    form.state.values.cities.length > 0 ||
    form.state.values.provinces.length > 0 ||
    form.state.values.onlyActives
  const activeCount = [
    form.state.values.pastorId.length,
    form.state.values.cities.length,
    form.state.values.provinces.length,
    form.state.values.onlyActives ? 1 : 0,
  ].filter(Boolean).length

  const filterForm = (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      {isMobile && (
        <form.Field
          name="search"
          children={(field) => (
            <IconInput
              placeholder="Search by name, pastor, city"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              className="w-full"
            />
          )}
        />
      )}
      <form.Field
        name="cities"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Cities"
            id={field.name}
            name={field.name}
            options={Array.from(citiesOptions).map((c) => ({
              value: c,
              label: c,
            }))}
            multiple
          />
        )}
      />
      <form.Field
        name="provinces"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Provinces"
            id={field.name}
            name={field.name}
            options={Array.from(provincesOptions).map((p) => ({
              value: p,
              label: p,
            }))}
            multiple
          />
        )}
      />
      <form.Field
        name="pastorId"
        children={(field) => (
          <AutoCompleteSelect
            field={field}
            label="Pastors"
            id={field.name}
            name={field.name}
            options={pastors.map((p) => ({ value: p._id, label: p.name }))}
            multiple
          />
        )}
      />
      <form.Field
        name="onlyActives"
        children={(field) => {
          return (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id={field.name}
                name={field.name}
                checked={field.state.value}
                onChange={(e) => field.handleChange(e.target.checked)}
                className="accent-primary h-4 w-4"
              />
              <label
                htmlFor={field.name}
                className="text-sm select-none cursor-pointer"
              >
                Show only active churches
              </label>
            </div>
          )
        }}
      />
      <form.Subscribe
        selector={(state) => [
          state.canSubmit,
          state.values.pastorId,
          state.values.cities,
          state.values.provinces,
          state.values.onlyActives,
          state.values.search,
        ]}
        children={([
          canSubmit,
          pastorIdValue,
          citiesValue,
          provincesValue,
          onlyActivesValue,
          searchValue,
        ]) => {
          const hasFilter =
            (Array.isArray(pastorIdValue) && pastorIdValue.length) ||
            (Array.isArray(citiesValue) && citiesValue.length) ||
            (Array.isArray(provincesValue) && provincesValue.length) ||
            onlyActivesValue
          const hasMobileSearch = isMobile && searchValue
          return (
            <div
              className={
                !isMobile ? 'flex gap-2 justify-end pt-2' : 'space-y-2'
              }
            >
              <Button type="button" variant="secondary" onClick={handleClear}>
                Clear
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || (!hasFilter && !hasMobileSearch)}
              >
                Apply
              </Button>
            </div>
          )
        }}
      />
    </form>
  )

  if (isMobile) {
    return <div className="w-full">{filterForm}</div>
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={filtersActive ? 'relative' : ''}>
          <FilterIcon size={16} />
          Filters
          {filtersActive && <Badge color="red">{activeCount}</Badge>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="p-4 min-w-[320px] max-w-100">
        {filterForm}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
